import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Layout, Button, Tooltip } from 'antd';
import { NativeButtonProps } from 'antd/lib/button/button';
import { useDispatch, useSelector } from 'react-redux';
import { SwapOutlined, BuildOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';

import { breakpoints, palette } from 'theme/variables';
import {
    getActiveOrganisation,
    getOrganisations,
    getUserRole,
} from 'features/user/selectors/userSelectors';
import { toggleUserOrganisationSelectModal } from 'features/user/actions/userActions';

import { MenuContent } from './MenuContent';
import { Logo } from '.';

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 72;

const Sider = styled(Layout.Sider)<{ $collapsed: boolean }>`
    &&& {
        display: none;
        background: #132b1c;
        min-width: ${({ $collapsed }) =>
            $collapsed ? `${COLLAPSED_WIDTH}px` : `${EXPANDED_WIDTH}px`} !important;
        max-width: ${({ $collapsed }) =>
            $collapsed ? `${COLLAPSED_WIDTH}px` : `${EXPANDED_WIDTH}px`} !important;
        width: ${({ $collapsed }) =>
            $collapsed ? `${COLLAPSED_WIDTH}px` : `${EXPANDED_WIDTH}px`} !important;
        flex: 0 0
            ${({ $collapsed }) => ($collapsed ? `${COLLAPSED_WIDTH}px` : `${EXPANDED_WIDTH}px`)} !important;
        position: sticky;
        top: 0;
        height: 100vh;
        /* Allow the edge-tab toggle to stick out on the right.
           Inner scrolling handled by MenuWrapper. */
        overflow: visible;
        transition: width 0.22s cubic-bezier(0.4, 0, 0.2, 1),
            min-width 0.22s cubic-bezier(0.4, 0, 0.2, 1),
            max-width 0.22s cubic-bezier(0.4, 0, 0.2, 1),
            flex-basis 0.22s cubic-bezier(0.4, 0, 0.2, 1);

        ${breakpoints.medium} {
            display: block;
        }

        /* forest → lime → gold gradient stripe between sidebar and main content,
           mirrors the right-edge accent on the login brand panel. */
        &::after {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            width: 4px;
            background: linear-gradient(
                to bottom,
                ${palette.forest} 0%,
                ${palette.lime} 55%,
                ${palette.gold} 100%
            );
            pointer-events: none;
        }

        .ant-layout-sider-children {
            display: flex;
            flex-direction: column;
            height: 100%;
        }
    }
`;

const SiderHeader = styled.div`
    padding: 0 14px 4px;
`;

/* Edge-tab toggle: sits on the right seam of the sidebar, half-off, so it's
   always visible and tactile (VS Code / Discord style). Floats over the
   gradient stripe rather than taking menu real estate. */
const EdgeToggle = styled.button`
    position: absolute;
    top: 72px;
    right: -12px;
    z-index: 20;
    width: 24px;
    height: 28px;
    border-radius: 6px;
    border: 1px solid #d9d3c2;
    background: #fff;
    color: #1e7a45;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
    transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;

    &:hover {
        background: #f7f4ee;
        color: #176338;
        transform: translateX(1px);
    }

    .anticon {
        font-size: 12px;
    }
`;

const OrgButton = styled((props: NativeButtonProps) => <Button {...props} />)`
    &&& {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        width: 100%;
        height: 38px;
        border-radius: 999px !important;
        font-size: 0.82rem;
        font-family: 'Inter', sans-serif;
        background: rgba(255, 255, 255, 0.08) !important;
        border: 1px solid rgba(255, 255, 255, 0.15) !important;
        color: #fff !important;
        transition: opacity 0.15s ease;

        .anticon,
        span {
            color: #fff !important;
        }

        &:hover,
        &:focus {
            background: rgba(255, 255, 255, 0.08) !important;
            border-color: rgba(255, 255, 255, 0.15) !important;
            color: #fff !important;
            opacity: 0.85;
        }
    }
`;

const OrgButtonCollapsed = styled(OrgButton)`
    &&& {
        width: 40px;
        height: 40px;
        padding: 0;
        gap: 0;

        .anticon {
            font-size: 16px;
        }
    }
`;

const OrgName = styled.div`
    text-align: center;
    padding: 8px 12px 0;
    color: rgba(255, 255, 255, 0.35);
    font-size: 0.75rem;
    font-family: 'Inter', sans-serif;
    letter-spacing: 0.2px;
    word-break: break-word;
    line-height: 1.4;
`;

const Separator = styled.div`
    height: 1px;
    background: rgba(255, 255, 255, 0.06);
    margin: 12px 16px;
`;

const MenuWrapper = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
`;

type Props = {
    hideMenu: boolean;
};

const COLLAPSED_STORAGE_KEY = 'cenefits.sidebar.collapsed';

const readPersistedCollapsed = (): boolean => {
    try {
        return localStorage.getItem(COLLAPSED_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

export const SideMenu = ({ hideMenu }: Props) => {
    const dispatch = useDispatch();
    const [collapsed, setCollapsed] = useState<boolean>(readPersistedCollapsed);

    const userRole = useSelector(getUserRole);
    const organisations = useSelector(getOrganisations);
    const activeOrganisation = useSelector(getActiveOrganisation);

    // Persist collapsed state across reloads.
    useEffect(() => {
        try {
            localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed));
        } catch {
            /* localStorage may be disabled (private mode); silently ignore. */
        }
    }, [collapsed]);

    // Cmd/Ctrl+B toggles the sidebar (Linear / VS Code convention).
    useEffect(() => {
        const handler = (event: KeyboardEvent) => {
            const isToggleCombo =
                (event.metaKey || event.ctrlKey) &&
                !event.shiftKey &&
                !event.altKey &&
                event.key.toLowerCase() === 'b';
            if (!isToggleCombo) {
                return;
            }
            // Don't hijack the shortcut while the user is typing into a field.
            const target = event.target as HTMLElement | null;
            const isTyping =
                target instanceof HTMLInputElement ||
                target instanceof HTMLTextAreaElement ||
                target?.isContentEditable;
            if (isTyping) {
                return;
            }
            event.preventDefault();
            setCollapsed((prev) => !prev);
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    return (
        <Sider $collapsed={collapsed}>
            <Logo collapsed={collapsed} />
            {!hideMenu && userRole && (
                <>
                    <Tooltip
                        title={
                            <>
                                {collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                                <kbd
                                    style={{
                                        marginLeft: 8,
                                        padding: '1px 6px',
                                        borderRadius: 4,
                                        background: 'rgba(255,255,255,0.15)',
                                        fontSize: 11,
                                    }}
                                >
                                    ⌘ B
                                </kbd>
                            </>
                        }
                        placement="right"
                    >
                        <EdgeToggle
                            onClick={() => setCollapsed((prev) => !prev)}
                            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        >
                            {collapsed ? <RightOutlined /> : <LeftOutlined />}
                        </EdgeToggle>
                    </Tooltip>
                    {organisations.length > 1 && (
                        <SiderHeader>
                            {collapsed ? (
                                <Tooltip title="Switch Organisation" placement="right">
                                    <OrgButtonCollapsed
                                        type="default"
                                        onClick={() =>
                                            dispatch(toggleUserOrganisationSelectModal(true))
                                        }
                                    >
                                        <SwapOutlined />
                                    </OrgButtonCollapsed>
                                </Tooltip>
                            ) : (
                                <OrgButton
                                    type="default"
                                    onClick={() =>
                                        dispatch(toggleUserOrganisationSelectModal(true))
                                    }
                                >
                                    <SwapOutlined />
                                    Switch Organisation
                                </OrgButton>
                            )}
                        </SiderHeader>
                    )}
                    {activeOrganisation && !collapsed && (
                        <SiderHeader>
                            <OrgName>
                                <BuildOutlined style={{ marginRight: 6, fontSize: 11 }} />
                                {activeOrganisation.organisation.name}
                            </OrgName>
                        </SiderHeader>
                    )}
                    <Separator />
                    <MenuWrapper>
                        <MenuContent collapsed={collapsed} />
                    </MenuWrapper>
                </>
            )}
        </Sider>
    );
};
